import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { emailRegex, isValidPhoneNumber } from "@constants/validation"
import { useUserPageContext } from "@contexts/main/users_page_context"
import { createUsers } from "@domain/users"
import useControlledForm from "@hooks/use_controlled_form"
import { Box, Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { RoleTypes } from "__generated__/graphql"

export type UserFormData = {
  firstName: string
  lastName: string
  email: string
  password: string
  phone: {
    value: string
    dialCode?: string
    countryCode: string
  }
  role: string
}

export default function CreateUser({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, setUsersData } = useUserPageContext()

  const schema = yup
    .object({
      firstName: yup
        .string()
        .min(2, translate("first_name_must_be_atleast_2_characters"))
        .required(translate("first_name_field_is_required")),
      lastName: yup
        .string()
        .min(2, translate("last_name_must_be_atleast_2_characters"))
        .required(translate("last_name_field_is_required")),
      password: yup
        .string()
        .min(6, translate("password_must_be_atleast_6_characters"))
        .required(translate("password_field_is_required")),
      email: yup
        .string()
        .matches(emailRegex, translate("email_error_invalid_email"))
        .email(translate("email_error_invalid_email"))
        .required(translate("email_required")),
      role: yup.string().required(translate("role_required")),
      phone: yup
        .object()
        .shape({
          value: yup.string().required(translate("phone_number_is_not_valid")),
          dialCode: yup.string(),
          countryCode: yup
            .string()
            .required(translate("phone_number_is_not_valid"))
        })
        .test(
          "phone_number_is_not_valid",
          translate("phone_number_is_not_valid"),
          isValidPhoneNumber
        )
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<UserFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      phone: {
        value: "90",
        countryCode: "tr",
        dialCode: "90"
      }
    }
  })

  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: UserFormData) {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await createUsers({
      name: data.firstName,
      surname: data.lastName,
      email: data.email,
      phone: data.phone.value,
      password: data.password,
      role: data.role as RoleTypes
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    const result = responseData.data

    setUsersData?.((prevUsersData) => [result, ...prevUsersData])
    queryClient.invalidateQueries({ queryKey: ["users-data-table"] })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("create_user")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <Box
          sx={{
            gap: 2,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" }
          }}
        >
          <TextInput<UserFormData>
            label={translate("first_name")}
            disabled={loading}
            name="firstName"
            autoComplete="given-name"
            placeholder={translate("first_name")}
            control={control}
            required
          />
          <TextInput<UserFormData>
            label={translate("last_name")}
            disabled={loading}
            name="lastName"
            control={control}
            autoComplete="family-name"
            placeholder={translate("last_name")}
            required
          />
        </Box>
        <TextInput<UserFormData>
          disabled={loading}
          type="email"
          name="email"
          control={control}
          label={translate("email")}
          autoComplete="email"
          placeholder={translate("email")}
          required
        />
        <TextInput<UserFormData>
          type="phone"
          name="phone"
          disabled={loading}
          label={translate("phone")}
          placeholder={translate("phone")}
          control={control}
          required
        />
        <TextInput<UserFormData>
          type="password"
          name="password"
          disabled={loading}
          label={translate("password")}
          placeholder={translate("password")}
          control={control}
          required
        />
        <TextInput
          type="select"
          disabled={loading}
          name="role"
          control={control}
          label={translate("role")}
          placeholder={translate("role")}
          selectOptions={[
            { label: "Admin", value: "ADMIN" },
            { label: "User", value: "USER" }
          ]}
          required
        />
      </Stack>
    </DialogProvider>
  )
}
